import $ from 'jquery';
import { Turbo } from '@hotwired/turbo-rails';
import { modalContainer } from '@/js/modules/modal-mount';
import confirmDialog from '@/js/modules/confirm_dialog';
import '@/js/modules/jquery_modal_defaults';
import { flashMessage } from '@/js/modules/admin_flash';
import { afterStreamRender } from '@/js/util/turbo';

Turbo.config.forms.confirm = (message) => confirmDialog(message);

/**
 * Turbo announces when a page or frame has rendered (`turbo:render`, `turbo:frame-render`), but not
 * a stream, so each `<turbo-stream>` dispatches `turbo:custom:stream-render` once it has rendered.
 * The event bubbles from the stream element, so `event.target` and `event.detail.newStream` are
 * the stream. Listeners cannot delay the render; use `beforeStreamRender` for that.
 */
document.addEventListener(
  'turbo:before-stream-render',
  afterStreamRender((stream) => {
    const detail = { newStream: stream };
    stream.dispatchEvent(new CustomEvent('turbo:custom:stream-render', { bubbles: true, detail }));
  })
);

/**
 * Custom action that logs a message to the browser console.
 * @this StreamElement
 * */
Turbo.StreamActions.console_log = function () {
  console.log(this.target);
};

/**
 * Custom action that scrolls given container to top.
 * @this StreamElement
 * */
Turbo.StreamActions.scroll_to_top = function () {
  const id = this.target;
  const element = document.getElementById(id);

  if (!element) {
    throw new Error(`turbo_stream.scroll_to_top: Element with id=${id} cannot be found on a page`);
  }

  element.scrollTop = 0;
};

/**
 * Custom action that opens modal.
 * @this StreamElement
 * */
Turbo.StreamActions.open_modal = function () {
  const id = this.target;
  let showClose = true;
  let modal;

  if (id) {
    modal = $(`#${id}`);
  } else {
    modal = $(this.templateContent.firstElementChild);
    // don't remove immediately, give some time for events to bubble up
    modal.one($.modal.AFTER_CLOSE, ({ target }) => setTimeout(target.remove.bind(target), 1000));
    const dataShowClose = this.getAttribute('data-show-close');
    if (dataShowClose) {
      showClose = dataShowClose === 'true';
    }
  }

  modal.is(':visible') || modal.modal({ showClose });

  const focusElement = modal.find('[data-js-focus-on-open]')[0];
  focusElement?.focus();

  const length = focusElement?.value?.length;
  length && focusElement.setSelectionRange(length, length);
};

/**
 * Custom action that closes modal
 * */
Turbo.StreamActions.close_modal = function () {
  $.modal.close();
};

/**
 * Custom action that opens a twin modal: appends the template content to the modal container,
 * and the dialog opens itself when it connects.
 * @this StreamElement
 * */
Turbo.StreamActions.open_dialog = function () {
  const content = this.templateContent.firstElementChild.cloneNode(true);
  modalContainer().appendChild(content);
};

/**
 * Custom action that closes the dialog around the target frame, or the current one when the target is "current".
 * @this StreamElement
 * */
Turbo.StreamActions.close_dialog = function () {
  if (this.target !== 'current') {
    document
      .getElementById(this.target)
      ?.closest('dialog[data-controller~="ui--twin-turbo-modal"]')
      ?.close();

    return;
  }

  const twins = document.querySelectorAll('dialog[data-controller~="ui--twin-turbo-modal"][open]');
  twins[twins.length - 1]?.close();
};

/**
 * Custom action that shows the admin layout's flash, like the legacy flashMessage helper.
 * Usage: turbo_stream.admin_flash("Saved") or turbo_stream.admin_flash("Failed", kind: :alert)
 * @this StreamElement
 * */
Turbo.StreamActions.admin_flash = function () {
  if (!document.querySelector('[data-admin-flash]')) {
    throw new Error('turbo_stream.admin_flash: no [data-admin-flash] element on this page');
  }

  flashMessage(this.getAttribute('message'), this.getAttribute('kind'));
};

/**
 * Custom action that performs a Turbo visit (client-side redirect).
 * Usage: turbo_stream.action(:redirect, url)
 * @this StreamElement
 * */
Turbo.StreamActions.redirect = function () {
  Turbo.visit(this.target);
};

let leavingForms = [];

const dropLeavingSubmit = (event) => {
  if (leavingForms.includes(event.target)) {
    event.preventDefault();
  }
};

const closeRestoredDialogs = (event) => {
  if (!event.persisted) {
    return;
  }

  leavingForms.forEach((form) => form.closest('dialog').close());
  leavingForms = [];
  document.removeEventListener('submit', dropLeavingSubmit, { capture: true });
  window.removeEventListener('pageshow', closeRestoredDialogs);
};

/**
 * Custom action that performs a genuine full page reload to a URL (sets
 * window.location, no Turbo snapshot visit). Use this when the destination
 * relies on legacy full-page-reload JS that re-initializes on load — e.g.
 * forms rendered inside a Turbo Frame (the share modal), whose redirects
 * Turbo would otherwise follow as in-place visits.
 * Usage: turbo_stream.action(:hard_redirect, url)
 * @this StreamElement
 * */
Turbo.StreamActions.hard_redirect = function () {
  // Turbo marks the submitting form idle at turbo:submit-end, while this page is still on screen
  // until the next one loads: keep open dialogs busy and drop their new submits so none goes out
  // twice. Back/forward cache can show the page again with those dialogs open, so pageshow closes
  // them, as their action already ran.
  leavingForms.push(...document.querySelectorAll('dialog[open] form'));
  leavingForms.forEach((form) => form.setAttribute('aria-busy', 'true'));
  document.addEventListener('submit', dropLeavingSubmit, { capture: true });
  window.addEventListener('pageshow', closeRestoredDialogs);
  window.location.href = this.target;
};

/**
 * Custom action that reloads the current page, keeping its URL and query string.
 * Use this when the user should stay where they are and the page relies on legacy
 * full-page-reload JS to pick up the change — e.g. recording a purchase order
 * payment from the order tracker, whose origin the billable redirect params
 * cannot express. The target is unused; the destination is always "here".
 * Usage: turbo_stream.action(:hard_reload, "")
 * @this StreamElement
 * */
Turbo.StreamActions.hard_reload = function () {
  window.location.reload();
};

/**
 * Custom action to trigger a cancel event on a given target
 * @this StreamElement
 * */
Turbo.StreamActions.cancel = function () {
  const target = $(this.target)[0];
  const event = new CustomEvent('cancel');

  target?.dispatchEvent(event);
};

Turbo.StreamActions.insert_billable_items = function () {
  const position = this.getAttribute('position');
  const target = this.targetElements[0];

  if (!target) {
    return;
  }

  const container = ['before', 'after'].includes(position) ? target.parentElement : target;
  const lastRowBeforeInsert =
    position === 'after'
      ? target
      : ([...container.querySelectorAll(':scope > .billable-item')].at(-1) ?? null);

  Turbo.StreamActions[position].call(this);

  target.dispatchEvent(
    new CustomEvent('billable-items:inserted', {
      bubbles: true,
      detail: { container, lastRowBeforeInsert },
    })
  );
};
